import React, { useCallback, useEffect, useState } from "react";
import apiClient from "../../utils/apiClient.js";
import { useAdmin } from "../../context/AdminContext.js";
import NotificationModal from "../../components/NotificationModal.js";
import useNotification from "../../hooks/useNotification.js";
import Spinner from "../../components/Spinner.js";
import Pagination from "../../components/Pagination.js";
import "./Admin.css";

const ROLES = ["super_admin", "admin", "staff"];
const PER_PAGE = 10;

const formatDateTime = (value) => (value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "Never");

const STRENGTH_LEVELS = ["Weak", "Fair", "Good", "Strong"];

// Simple heuristic, UX guidance only — the server's 8-character minimum is the real requirement.
const passwordStrength = (value) => {
  if (!value) return 0;
  let score = 0;
  if (value.length >= 8) score += 1;
  if (value.length >= 12) score += 1;
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
  if (/\d/.test(value) && /[^A-Za-z0-9]/.test(value)) score += 1;
  return Math.min(score, 4);
};

const AdminsPage = () => {
  const { admin: currentAdmin } = useAdmin();
  const [admins, setAdmins] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { notification, notify, dismiss } = useNotification();
  const [savingId, setSavingId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [creating, setCreating] = useState(false);

  const [resetTarget, setResetTarget] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState("");
  const [resetConfirmValue, setResetConfirmValue] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.get("/admins", { params: { page, perPage: PER_PAGE } });
      setAdmins(data.data);
      setTotal(data.total);
    } catch (err) {
      setError("Could not load admin accounts.");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await apiClient.post("/admins", form);
      notify("success", "Account created", `A new admin account was created for ${form.email}.`);
      setForm({ name: "", email: "", password: "", role: "staff" });
      load();
    } catch (err) {
      notify("error", "Could not create this admin account", err.response?.data?.error || "Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (id, role) => {
    setSavingId(id);
    try {
      await apiClient.post(`/admins/${id}`, { role });
      setAdmins((prev) => prev.map((a) => (a.id === id ? { ...a, role } : a)));
      notify("success", "Role updated", `Account role changed to ${role.replace("_", " ")}.`);
    } catch (err) {
      notify("error", "Could not update this account", err.response?.data?.error || "Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  const handleToggleActive = async (id, isActive) => {
    setSavingId(id);
    try {
      await apiClient.post(`/admins/${id}`, { isActive });
      setAdmins((prev) => prev.map((a) => (a.id === id ? { ...a, is_active: isActive ? 1 : 0 } : a)));
      notify("success", isActive ? "Account activated" : "Account deactivated", null);
    } catch (err) {
      notify("error", "Could not update this account", err.response?.data?.error || "Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (admin) => {
    if (
      // eslint-disable-next-line no-restricted-globals
      !window.confirm(
        `Permanently delete ${admin.name} (${admin.email})? This cannot be undone — they will lose access immediately.`
      )
    ) {
      return;
    }

    setSavingId(admin.id);
    try {
      await apiClient.delete(`/admins/${admin.id}`);
      setAdmins((prev) => prev.filter((a) => a.id !== admin.id));
      notify("success", "Account deleted", `${admin.name}'s account has been removed.`);
    } catch (err) {
      notify("error", "Could not delete this account", err.response?.data?.error || "Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  const openResetPassword = (admin) => {
    setResetTarget(admin);
    setResetPasswordValue("");
    setResetConfirmValue("");
    setShowResetPassword(false);
  };

  const closeResetPassword = () => {
    if (resetting) return;
    setResetTarget(null);
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (resetPasswordValue.length < 8) {
      notify("warning", "Password too short", "New password must be at least 8 characters.");
      return;
    }
    if (resetPasswordValue !== resetConfirmValue) {
      notify("warning", "Passwords don't match", "Re-enter the new password to confirm it.");
      return;
    }
    if (
      // eslint-disable-next-line no-restricted-globals
      !window.confirm(`Reset the password for ${resetTarget.name} (${resetTarget.email})? They will need to sign in with this new password.`)
    ) {
      return;
    }

    setResetting(true);
    try {
      await apiClient.post(`/admins/${resetTarget.id}/reset-password`, { newPassword: resetPasswordValue });
      notify("success", "Password reset", `${resetTarget.name}'s password has been changed.`);
      setResetTarget(null);
    } catch (err) {
      notify("error", "Could not reset this password", err.response?.data?.error || "Please try again.");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Admin Accounts</h1>
        <p>{total} total</p>
      </div>

      <form className="admin-create-form" onSubmit={handleCreate}>
        <h2>Add a new admin</h2>
        <div className="admin-create-fields">
          <input
            type="text"
            placeholder="Full name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            required
          />
          <input
            type="password"
            placeholder="Temporary password (min 8 chars)"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            minLength={8}
            required
          />
          <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r.replace("_", " ")}
              </option>
            ))}
          </select>
          <button type="submit" disabled={creating}>
            {creating ? "Creating…" : "Create account"}
          </button>
        </div>
      </form>

      {error && <p className="admin-error">{error}</p>}

      {loading ? (
        <Spinner label="Loading admins…" />
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>S.N.</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Last login</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a, index) => {
                const isSelf = a.id === currentAdmin?.id;
                return (
                  <tr key={a.id}>
                    <td>{(page - 1) * PER_PAGE + index + 1}</td>
                    <td className="admin-ellipsis-cell" title={a.name}>{a.name}</td>
                    <td className="admin-ellipsis-cell" title={a.email}>{a.email}</td>
                    <td>
                      <select
                        value={a.role}
                        disabled={isSelf || savingId === a.id}
                        onChange={(e) => handleRoleChange(a.id, e.target.value)}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r.replace("_", " ")}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{formatDateTime(a.last_login_at)}</td>
                    <td>
                      <button
                        type="button"
                        className={a.is_active ? "admin-active-btn" : "admin-inactive-btn"}
                        disabled={isSelf || savingId === a.id}
                        onClick={() => handleToggleActive(a.id, !a.is_active)}
                      >
                        {a.is_active ? "Active" : "Deactivated"}
                      </button>
                    </td>
                    <td className="admin-actions-cell">
                      <div className="admin-cell-flex">
                        {!isSelf && (
                          <>
                            <button type="button" className="admin-resend-link" onClick={() => openResetPassword(a)}>
                              Reset password
                            </button>
                            <button
                              type="button"
                              className="admin-delete-btn"
                              disabled={savingId === a.id}
                              onClick={() => handleDelete(a)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination page={page} perPage={PER_PAGE} total={total} onPageChange={setPage} />
        </div>
      )}

      {resetTarget && (
        <div className="notify-modal-backdrop" onClick={closeResetPassword}>
          <div
            className="notify-modal admin-reset-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reset-password-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="reset-password-title">Reset password</h3>
            <p className="notify-modal-message">
              Set a new password for <strong>{resetTarget.name}</strong> ({resetTarget.email}).
            </p>
            <form onSubmit={handleResetPasswordSubmit}>
              <div className="admin-form-group">
                <label htmlFor="reset-password-value">New password</label>
                <div className="admin-password-input-wrap">
                  <input
                    id="reset-password-value"
                    type={showResetPassword ? "text" : "password"}
                    value={resetPasswordValue}
                    onChange={(e) => setResetPasswordValue(e.target.value)}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    autoFocus
                  />
                  <button
                    type="button"
                    className="admin-password-toggle-btn"
                    onClick={() => setShowResetPassword((v) => !v)}
                    aria-label={showResetPassword ? "Hide password" : "Show password"}
                  >
                    {showResetPassword ? "Hide" : "Show"}
                  </button>
                </div>
                {resetPasswordValue && (
                  <div className="admin-password-strength">
                    <div className="admin-password-strength-bar">
                      <div
                        className={`admin-password-strength-fill admin-password-strength-${passwordStrength(resetPasswordValue)}`}
                      />
                    </div>
                    <span className="admin-password-strength-label">
                      {STRENGTH_LEVELS[Math.max(passwordStrength(resetPasswordValue) - 1, 0)]}
                    </span>
                  </div>
                )}
              </div>
              <div className="admin-form-group">
                <label htmlFor="reset-password-confirm">Confirm new password</label>
                <input
                  id="reset-password-confirm"
                  type={showResetPassword ? "text" : "password"}
                  value={resetConfirmValue}
                  onChange={(e) => setResetConfirmValue(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </div>
              <div className="admin-reset-modal-actions">
                <button type="button" className="admin-login-back-btn" onClick={closeResetPassword} disabled={resetting}>
                  Cancel
                </button>
                <button type="submit" disabled={resetting}>
                  {resetting ? "Resetting…" : "Reset password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {notification && (
        <NotificationModal
          variant={notification.variant}
          title={notification.title}
          message={notification.message}
          onClose={dismiss}
        />
      )}
    </div>
  );
};

export default AdminsPage;
